import React, { useEffect, useState } from 'react';
import { jobService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import { Briefcase, MapPin, Clock, IndianRupee, Upload, CheckCircle, ArrowLeft, Briefcase as BriefcaseIcon, Handshake, Users, GraduationCap, Star, Quote, AlertCircle } from 'lucide-react';

const CAREER_TYPES = [
  { key: 'employment', label: 'Employment', icon: BriefcaseIcon, desc: 'Full-time employment opportunities', color: 'from-red-600 to-orange-600' },
  { key: 'business_coordinator', label: 'Business Coordinator', icon: Handshake, desc: 'Coordinate business operations & partnerships', color: 'from-blue-600 to-cyan-600' },
  { key: 'business_associates', label: 'Business Associates', icon: Users, desc: 'Partner with us as a business associate', color: 'from-green-600 to-emerald-600' },
  { key: 'internship', label: 'Internship Programme', icon: GraduationCap, desc: 'Learn and grow with our internship programme', color: 'from-purple-600 to-pink-600' },
];

const TESTIMONIALS = {
  brand_ambassadors: [
    { name: 'Coming Soon', role: 'Brand Ambassador', quote: 'Testimonials will be added here.' },
  ],
  brand_endorsers: [
    { name: 'Coming Soon', role: 'Brand Endorser', quote: 'Testimonials will be added here.' },
  ],
  consumers: [
    { name: 'Coming Soon', role: 'Consumer', quote: 'Testimonials will be added here.' },
  ],
  dealers_retailers: [
    { name: 'Coming Soon', role: 'Dealer / Retailer', quote: 'Testimonials will be added here.' },
  ],
  distributor: [
    { name: 'Coming Soon', role: 'Distributor', quote: 'Testimonials will be added here.' },
  ],
  super_distributors: [
    { name: 'Coming Soon', role: 'Super Distributor', quote: 'Testimonials will be added here.' },
  ],
};

function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestimonialTab, setActiveTestimonialTab] = useState('brand_ambassadors');
  const [selectedJob, setSelectedJob] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'detail' | 'apply' | 'success'
  const [applyForm, setApplyForm] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    updateMetaTags({
      title: 'Careers | Ortus',
      description: 'Explore career opportunities at Ortus. Join our growing team.',
      siteName: 'Ortus'
    });
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await jobService.index({ status: 'active' });
      setJobs(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error('Error loading jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const openJobDetail = (job) => {
    setSelectedJob(job);
    setView('detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openApplyForm = (jobOrType) => {
    if (typeof jobOrType === 'string') {
      setSelectedType(jobOrType);
      setSelectedJob(null);
    } else {
      setSelectedJob(jobOrType);
      setSelectedType('employment');
    }
    setView('apply');
    setApplyForm({});
    setErrors({});
    setSubmitError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFieldChange = (name, value) => {
    setApplyForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fieldName]: 'File size must be less than 5MB' }));
      return;
    }
    setApplyForm(prev => ({ ...prev, [fieldName]: file }));
    setErrors(prev => ({ ...prev, [fieldName]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!applyForm.first_name?.trim()) errs.first_name = 'Applicant full name is required';
    if (!applyForm.email?.trim()) errs.email = 'Email ID is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applyForm.email)) errs.email = 'Please enter a valid email';
    if (!applyForm.phone?.trim()) errs.phone = 'Contact number is required';
    if (selectedType === 'employment' && !applyForm.resume_file) errs.resume_file = 'Please upload your updated resume';
    if ((selectedType === 'business_coordinator' || selectedType === 'business_associates' || selectedType === 'internship') && !applyForm.resume_file) errs.resume_file = 'Please upload your resume';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const formData = new FormData();
      // Application type
      formData.append('application_type', selectedType || 'employment');
      // Core fields
      formData.append('first_name', applyForm.first_name || '');
      formData.append('last_name', applyForm.last_name || '');
      formData.append('email', applyForm.email || '');
      formData.append('phone', applyForm.phone || '');
      formData.append('cover_letter', applyForm.cover_letter || '');
      // Personal details (all types)
      const personalFields = ['father_name','mother_name','nationality','state_region_birth','permanent_address','present_address','gender','date_of_birth','age','alternate_number','marital_status','interest_hobbies'];
      personalFields.forEach(f => formData.append(f, applyForm[f] || ''));
      // Professional details (employment + business_coordinator + business_associates)
      if (selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates') {
        const profFields = ['present_organization','current_tenure','present_designation','department_of_work','job_skill','job_responsibility','present_monthly_net_pay','present_ctc','total_years_experience','past_organizations','current_job_location','highest_qualification','institution_name'];
        profFields.forEach(f => formData.append(f, applyForm[f] || ''));
      }
      // Market Strength (business_coordinator + business_associates)
      if (selectedType === 'business_coordinator' || selectedType === 'business_associates') {
        const marketFields = ['retail_count_base','premium_retail_count_base','distributor_count_base','super_distributor_count_base','fofo_base','foco_base','ortus_franchise_base'];
        marketFields.forEach(f => formData.append(f, applyForm[f] || ''));
      }
      // Internship-specific fields
      if (selectedType === 'internship') {
        const internFields = ['college_name','affiliation','qualification_level','current_academic_year','subject_specialization','reason_for_internship','work_experience','type_of_job','highest_qualification','institution_name','passing_percentage','reason_selecting_company','why_internship','expected_stipend','ok_minimum_5_hours','ok_target_based_stipend','open_to_travel','driving_license'];
        internFields.forEach(f => formData.append(f, applyForm[f] || ''));
      }
      // Languages (all types)
      if (selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates' || selectedType === 'internship') {
        for (let i = 1; i <= 3; i++) {
          formData.append(`language${i}`, applyForm[`language${i}`] || '');
          formData.append(`language${i}_read`, applyForm[`language${i}_read`] || '');
          formData.append(`language${i}_write`, applyForm[`language${i}_write`] || '');
          formData.append(`language${i}_speak`, applyForm[`language${i}_speak`] || '');
        }
      }
      // Additional expertise (all types)
      ['computer_skills','professional_certification','key_strength'].forEach(f => formData.append(f, applyForm[f] || ''));
      // Why change / why join (non-internship)
      if (selectedType !== 'internship') {
        ['reason_for_application','preferred_job_location','expected_ctc'].forEach(f => formData.append(f, applyForm[f] || ''));
      }
      // Files
      if (applyForm.resume_file) formData.append('resume_file', applyForm.resume_file);
      if (applyForm.salary_slip_file) formData.append('salary_slip_file', applyForm.salary_slip_file);
      if (applyForm.photo_file) formData.append('photo_file', applyForm.photo_file);
      if (applyForm.aadhar_card_file) formData.append('aadhar_card_file', applyForm.aadhar_card_file);
      if (applyForm.last_passing_result_file) formData.append('last_passing_result_file', applyForm.last_passing_result_file);
      if (applyForm.student_id_file) formData.append('student_id_file', applyForm.student_id_file);
      if (applyForm.pan_card_file) formData.append('pan_card_file', applyForm.pan_card_file);

      const jobId = selectedJob?.id || 0;
      await jobService.apply(jobId, formData);
      setView('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const apiErr = err?.response?.data?.message || err?.response?.data?.errors;
      if (apiErr && typeof apiErr === 'object') {
        setErrors(apiErr);
      } else {
        setSubmitError(apiErr || 'Failed to submit application. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Competitive';
    if (min && max) return `₹${Number(min).toLocaleString()} - ₹${Number(max).toLocaleString()}`;
    return min ? `₹${Number(min).toLocaleString()}+` : `Up to ₹${Number(max).toLocaleString()}`;
  };

  // ─── Success View ──────────────────────────────────
  if (view === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 flex items-center justify-center px-4 py-20">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-4">Application Submitted!</h1>
          <p className="text-slate-600 leading-relaxed mb-8">
            Thank you for applying to <strong>{selectedJob?.title || CAREER_TYPES.find(t => t.key === selectedType)?.label}</strong> at Ortus.
            Our team will review your application and contact you if your profile matches our requirements.
          </p>
          <button
            onClick={() => { setView('list'); setSelectedJob(null); loadJobs(); }}
            className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3 rounded-xl transition shadow-lg hover:scale-105"
          >
            Back to Job Listings
          </button>
        </div>
      </div>
    );
  }

  // ─── Apply Form View ──────────────────────────────────
  if (view === 'apply') {
    const inputCls = (err) => `w-full bg-white border rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${err ? 'border-red-400 bg-red-50/30' : 'border-slate-200 hover:border-slate-300'}`;
    const labelCls = 'block text-sm font-semibold text-slate-700 mb-1.5';
    const sectionCls = 'bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-7';
    const sectionTitleCls = 'text-base font-bold text-slate-900 mb-5 flex items-center gap-3';
    const typeLabel = CAREER_TYPES.find(t => t.key === selectedType)?.label || 'Employment';
    const typeDesc = CAREER_TYPES.find(t => t.key === selectedType)?.desc || '';
    const typeColor = CAREER_TYPES.find(t => t.key === selectedType)?.color || 'from-red-600 to-orange-600';
    const TypeIcon = CAREER_TYPES.find(t => t.key === selectedType)?.icon || BriefcaseIcon;

    const renderInput = (name, label, type = 'text', required = false, placeholder = '') => (
      <div>
        <label className={labelCls}>{label} {required && <span className="text-red-500">*</span>}</label>
        <input
          type={type}
          value={applyForm[name] || ''}
          onChange={(e) => handleFieldChange(name, e.target.value)}
          className={inputCls(errors[name])}
          placeholder={placeholder || `Enter ${label.toLowerCase()}`}
        />
        {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
      </div>
    );

    const renderTextarea = (name, label, required = false) => (
      <div>
        <label className={labelCls}>{label} {required && <span className="text-red-500">*</span>}</label>
        <textarea
          rows={2}
          value={applyForm[name] || ''}
          onChange={(e) => handleFieldChange(name, e.target.value)}
          className={inputCls(errors[name])}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
      </div>
    );

    const renderSelect = (name, label, options, required = false) => (
      <div>
        <label className={labelCls}>{label} {required && <span className="text-red-500">*</span>}</label>
        <select
          value={applyForm[name] || ''}
          onChange={(e) => handleFieldChange(name, e.target.value)}
          className={inputCls(errors[name])}
        >
          <option value="">Select {label}</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
      </div>
    );

    const renderFileUpload = (name, label, accept, required = false) => (
      <div>
        <label className={labelCls}>{label} {required && <span className="text-red-500">*</span>}</label>
        <div className={`relative border-2 border-dashed rounded-xl px-3 py-5 text-center transition-all ${errors[name] ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-red-400 hover:bg-red-50/30'}`}>
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileChange(e, name)}
            className="hidden"
            id={`${name}-upload`}
          />
          <label htmlFor={`${name}-upload`} className="cursor-pointer flex flex-col items-center gap-1.5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${applyForm[name] ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
              {applyForm[name] ? <CheckCircle className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
            </div>
            {applyForm[name] ? (
              <span className="text-xs text-green-600 font-semibold truncate max-w-full">{applyForm[name].name}</span>
            ) : (
              <span className="text-xs text-slate-500 font-medium">Click to upload</span>
            )}
          </label>
        </div>
        {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
      </div>
    );

    const renderLanguageRow = (num) => (
      <div className="grid grid-cols-4 gap-3">
        {renderInput(`language${num}`, `Language ${num}`)}
        <div className="flex items-center gap-2 pt-6">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={applyForm[`language${num}_read`] === 'Yes'}
              onChange={(e) => handleFieldChange(`language${num}_read`, e.target.checked ? 'Yes' : '')}
              className="w-4 h-4 accent-red-500"
            />
            Read
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={applyForm[`language${num}_write`] === 'Yes'}
              onChange={(e) => handleFieldChange(`language${num}_write`, e.target.checked ? 'Yes' : '')}
              className="w-4 h-4 accent-red-500"
            />
            Write
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={applyForm[`language${num}_speak`] === 'Yes'}
              onChange={(e) => handleFieldChange(`language${num}_speak`, e.target.checked ? 'Yes' : '')}
              className="w-4 h-4 accent-red-500"
            />
            Speak
          </label>
        </div>
      </div>
    );

    const sectionHeader = (num, title) => (
      <h2 className={sectionTitleCls}>
        <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 text-white text-sm font-black flex items-center justify-center shrink-0">{num}</span>
        {title}
      </h2>
    );

    return (
      <div className="min-h-screen bg-slate-100">
        {/* Hero Header */}
        <section className="relative bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 text-white py-12 md:py-16 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-10 blur-3xl rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500 opacity-10 blur-3xl rounded-full"></div>
          <div className="max-w-5xl mx-auto px-4 relative">
            <button onClick={() => selectedJob ? setView('detail') : setView('list')} className="flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> {selectedJob ? 'Back to Job Details' : 'Back to Careers'}
            </button>
            <div className="flex items-center gap-4 mb-3">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${typeColor} flex items-center justify-center text-white shadow-xl shrink-0`}>
                <TypeIcon size={28} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black leading-tight">Apply Online</h1>
                <p className="text-slate-300 text-sm mt-0.5">{typeLabel} · {typeDesc}</p>
              </div>
            </div>
            {selectedJob && (
              <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 text-sm">
                <Briefcase size={14} className="text-red-400" />
                <span className="font-semibold">{selectedJob.title}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-300">{selectedJob.location}</span>
              </div>
            )}
          </div>
        </section>

        {/* Form Body */}
        <section className="py-8 md:py-12">
          <div className="max-w-5xl mx-auto px-4">
            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl text-sm flex items-start gap-3">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{submitError}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6 lg:gap-8">
              {/* Form Fields */}
              <div className="space-y-5 min-w-0">
              {/* Personal Details (all types) */}
              <div className={sectionCls}>
                {sectionHeader(1, 'Personal Details')}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderInput('first_name', selectedType === 'internship' ? "Student's Full Name" : 'Applicant Full Name', 'text', true)}
                  {renderInput('father_name', "Father's Name")}
                  {renderInput('mother_name', "Mother's Name")}
                  {renderInput('nationality', 'Nationality')}
                  {renderInput('state_region_birth', 'State & Region of Birth')}
                  {renderTextarea('permanent_address', 'Permanent Address')}
                  {renderTextarea('present_address', 'Present Address')}
                  {renderSelect('gender', 'Gender', ['Male', 'Female', 'Other'])}
                  {renderInput('date_of_birth', 'Date of Birth', 'date')}
                  {renderInput('age', 'Age')}
                  {renderInput('phone', 'Contact Number', 'tel', true)}
                  {renderInput('alternate_number', 'Alternate Number', 'tel')}
                  {renderInput('email', 'Email ID', 'email', true)}
                  {renderSelect('marital_status', 'Marital Status', ['Single', 'Married', 'Divorced', 'Widowed'])}
                </div>
                <div className="mt-4">
                  {renderTextarea('interest_hobbies', 'Interest & Hobbies')}
                </div>
              </div>

              {/* Professional Details (employment + business_coordinator + business_associates) */}
              {(selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates') && (
                <div className={sectionCls}>
                  {sectionHeader(2, 'Professional Details')}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInput('present_organization', selectedType === 'employment' ? 'Present Organization' : 'Present Organization / Work')}
                    {renderInput('current_tenure', selectedType === 'employment' ? 'Current Tenure of Job' : 'Current Tenure of Job / Work')}
                    {renderInput('present_designation', 'Present Designation')}
                    {renderInput('department_of_work', selectedType === 'employment' ? 'Department of Work' : 'Department / Area of Work')}
                    {renderInput('job_skill', 'Job Skill')}
                    {renderTextarea('job_responsibility', 'Job Responsibility')}
                    {renderInput('present_monthly_net_pay', selectedType === 'employment' ? 'Present Monthly Net Pay' : 'Present Monthly Net Pay / Payout')}
                    {selectedType !== 'business_associates' && renderInput('present_ctc', selectedType === 'business_coordinator' ? 'Present Annual CTC (If any)' : 'Present CTC')}
                    {renderInput('total_years_experience', selectedType === 'employment' ? 'Total Years of Job Experience' : 'Total Years of Job / Work Experience')}
                    {renderTextarea('past_organizations', 'Organizations/Companies worked with in the past')}
                    {renderInput('current_job_location', selectedType === 'employment' ? 'Current Job Location' : 'Current Job / Work Location')}
                    {renderInput('highest_qualification', 'Highest Qualification')}
                    {renderInput('institution_name', 'Institution Name')}
                  </div>
                </div>
              )}

              {/* Market Strength (business_coordinator + business_associates) */}
              {(selectedType === 'business_coordinator' || selectedType === 'business_associates') && (
                <div className={sectionCls}>
                  {sectionHeader(3, 'Market Strength')}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedType === 'business_coordinator' && renderInput('retail_count_base', 'Retail Count Base (Investment - INR 2500 to 10000)')}
                    {selectedType === 'business_coordinator' && renderInput('premium_retail_count_base', 'Premium Retail Count Base (Minimum Investment INR 10000)')}
                    {selectedType === 'business_coordinator' && renderInput('distributor_count_base', 'Distributor Count Base (Investment upto INR 2.50 Lacs)')}
                    {selectedType === 'business_coordinator' && renderInput('super_distributor_count_base', 'Super Distributor Count Base (Minimum Investment - INR 5 Lacs to 10 Lacs)')}
                    {selectedType === 'business_associates' && renderInput('retail_count_base', 'Retail Count Base (Investment - INR 1000 to 5000)')}
                    {selectedType === 'business_associates' && renderInput('premium_retail_count_base', 'Premium Retail Count Base (Minimum Investment INR 5000)')}
                    {selectedType === 'business_associates' && renderInput('fofo_base', 'FOFO (Minimum Investment - INR 1.00 upto 2.00 Lacs)')}
                    {selectedType === 'business_associates' && renderInput('foco_base', 'FOCO (Minimum Investment - INR 2.50 Lacs to 5 Lacs)')}
                    {selectedType === 'business_associates' && renderInput('ortus_franchise_base', 'ORTUS Franchise (Minimum Investment - INR 5 Lacs)')}
                  </div>
                </div>
              )}

              {/* College / Institute Details (internship only) */}
              {selectedType === 'internship' && (
                <div className={sectionCls}>
                  {sectionHeader(2, 'College / Institute Details')}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInput('college_name', 'College / Institute Name')}
                    {renderInput('affiliation', 'Affiliation')}
                    {renderSelect('qualification_level', 'Post Graduate / Graduate', ['Post Graduate', 'Graduate', 'Under Graduate'])}
                    {renderInput('current_academic_year', 'Current Year of Academic Qualification')}
                    {renderInput('subject_specialization', 'Subject Specialization')}
                    {renderTextarea('reason_for_internship', 'Reason for Internship Application')}
                    {renderTextarea('work_experience', 'Any Work Experience (If any)')}
                    {renderInput('type_of_job', 'Type of Job')}
                    {renderInput('highest_qualification', 'Highest Qualification')}
                    {renderInput('institution_name', 'Institution Name')}
                    {renderInput('passing_percentage', 'Passing %')}
                  </div>
                </div>
              )}

              {/* Additional Expertise & Pillars (all types) */}
              {(selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates' || selectedType === 'internship') && (
                <div className={sectionCls}>
                  {sectionHeader(selectedType === 'internship' ? 3 : 4, 'Additional Expertise & Pillars')}
                  <div className="mb-4">
                    <label className={labelCls}>Language Known</label>
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                      <div className="grid grid-cols-4 gap-3 mb-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">
                        <span>Language</span>
                        <span className="col-span-3 text-center">Read · Write · Speak</span>
                      </div>
                      <div className="space-y-3">
                        {renderLanguageRow(1)}
                        {renderLanguageRow(2)}
                        {renderLanguageRow(3)}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderInput('computer_skills', 'Computer Skills')}
                    {renderInput('professional_certification', 'Professional Certification (if any)')}
                  </div>
                  <div className="mt-4">
                    {renderTextarea('key_strength', 'Key Strength')}
                  </div>
                </div>
              )}

              {/* Upload Attachments */}
              <div className={sectionCls}>
                {sectionHeader(selectedType === 'internship' ? 4 : 5, 'Upload Attachments')}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderFileUpload('resume_file', 'Updated Resume', '.pdf,.doc,.docx', true)}
                  {(selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates') && renderFileUpload('salary_slip_file', 'Last Salary / Payout Slip (If any)', '.pdf,.doc,.docx,.jpg,.jpeg,.png')}
                  {selectedType === 'internship' && renderFileUpload('last_passing_result_file', 'Last Passing Result', '.pdf,.doc,.docx,.jpg,.jpeg,.png')}
                  {selectedType === 'internship' && renderFileUpload('student_id_file', "College / Institute's Student ID", '.pdf,.doc,.docx,.jpg,.jpeg,.png')}
                  {renderFileUpload('photo_file', 'Latest Photograph', '.jpg,.jpeg,.png')}
                  {(selectedType === 'business_coordinator' || selectedType === 'business_associates' || selectedType === 'internship') && renderFileUpload('aadhar_card_file', 'Aadhar Card - Front & flip Side', '.pdf,.jpg,.jpeg,.png')}
                  {selectedType === 'internship' && renderFileUpload('pan_card_file', 'PAN', '.pdf,.jpg,.jpeg,.png')}
                </div>
              </div>

              {/* Why Change / Why Join */}
              {selectedType !== 'internship' && (
                <div className={sectionCls}>
                  {sectionHeader(6, selectedType === 'business_coordinator' || selectedType === 'business_associates' ? 'Why to Join Us?' : 'Why Change?')}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderTextarea('reason_for_application', selectedType === 'business_coordinator' ? 'Reason for Job / Work Application as Business Coordinator' : selectedType === 'business_associates' ? 'Reason for Job / Work Application as Business Associate' : 'Reason for Application')}
                    {renderInput('preferred_job_location', selectedType === 'employment' ? 'Preferred Location' : 'Preferred Job / Work Location')}
                    {renderInput('expected_ctc', selectedType === 'business_coordinator' ? 'Expected Payout / % on Monthly Business Billing' : selectedType === 'business_associates' ? 'Expected % on Monthly Business Billing' : 'Expected CTC')}
                  </div>
                </div>
              )}

              {/* Why do you want to do Internship with us? (internship only) */}
              {selectedType === 'internship' && (
                <div className={sectionCls}>
                  {sectionHeader(5, 'Why do you want to do Internship with us?')}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderTextarea('reason_selecting_company', 'Reason for selecting our company for the Internship?')}
                    {renderTextarea('why_internship', 'Why do you want to do Internship?')}
                    {renderInput('expected_stipend', 'What Payout / Stipend are you looking for?')}
                    {renderSelect('ok_minimum_5_hours', 'Are you OK with Minimum 5 hours of work?', ['Yes', 'No'])}
                    {renderSelect('ok_target_based_stipend', 'Are you OK with Target based Stipend or payout?', ['Yes', 'No'])}
                    {renderSelect('open_to_travel', 'Are you open to travel if needed?', ['Yes', 'No'])}
                    {renderInput('driving_license', 'Do you have a driving license? (If yes, Two-wheeler or Four-wheeler)')}
                  </div>
                </div>
              )}

              {/* Cover Letter */}
              <div className={sectionCls}>
                {sectionHeader(selectedType === 'internship' ? 6 : 7, 'Cover Letter (Optional)')}
                {renderTextarea('cover_letter', 'Additional Notes')}
              </div>
              </div>

              {/* Sticky Sidebar — Desktop only */}
              <div className="hidden lg:block">
                <div className="sticky top-6 space-y-4">
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${typeColor} flex items-center justify-center text-white mb-3`}>
                      <TypeIcon size={24} />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm">{typeLabel}</h3>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{typeDesc}</p>
                    {selectedJob && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs font-semibold text-slate-700">{selectedJob.title}</p>
                        <p className="text-xs text-slate-400">{selectedJob.location}</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                    <h4 className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-3">Form Sections</h4>
                    <ul className="space-y-2 text-xs">
                      <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">1</span> Personal Details</li>
                      {(selectedType === 'employment' || selectedType === 'business_coordinator' || selectedType === 'business_associates') && <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">2</span> Professional Details</li>}
                      {(selectedType === 'business_coordinator' || selectedType === 'business_associates') && <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">3</span> Market Strength</li>}
                      {selectedType === 'internship' && <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">2</span> College / Institute</li>}
                      <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">{selectedType === 'internship' ? '3' : '4'}</span> Expertise & Pillars</li>
                      <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">{selectedType === 'internship' ? '4' : '5'}</span> Upload Attachments</li>
                      <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">{selectedType === 'internship' ? '5' : '6'}</span> {selectedType === 'internship' ? 'Why Internship?' : 'Why Change?'}</li>
                      <li className="flex items-center gap-2 text-slate-600"><span className="w-5 h-5 rounded bg-red-50 text-red-500 text-[10px] font-bold flex items-center justify-center">{selectedType === 'internship' ? '6' : '7'}</span> Cover Letter</li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl p-5 text-white">
                    <p className="text-xs font-semibold leading-relaxed">Fields marked with <span className="font-black">*</span> are required. Please have your documents ready before starting.</p>
                  </div>
                </div>
              </div>
            </form>

            {/* Sticky Submit Bar */}
            <div className="sticky bottom-0 mt-6 -mx-4 px-4 py-4 bg-white/80 backdrop-blur-lg border-t border-slate-200 flex flex-col sm:flex-row gap-3 z-20">
              <button
                type="button"
                onClick={() => selectedJob ? setView('detail') : setView('list')}
                className="px-6 py-3 rounded-xl text-sm font-semibold text-slate-600 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold px-8 py-3 rounded-xl transition shadow-lg hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Submitting Application...
                  </>
                ) : (
                  <>Submit Application</>
                )}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── Job Detail View ──────────────────────────────────
  if (view === 'detail' && selectedJob) {
    return (
      <div className="min-h-screen bg-slate-50">
        <section className="bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <button onClick={() => { setView('list'); setSelectedJob(null); }} className="flex items-center gap-2 text-slate-400 hover:text-white transition mb-6 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to All Jobs
            </button>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-red-600/20 text-red-400 rounded-full text-xs font-bold uppercase tracking-wider">{selectedJob.type?.replace('_', ' ')}</span>
              {selectedJob.status === 'active' && (
                <span className="px-3 py-1 bg-green-600/20 text-green-400 rounded-full text-xs font-bold uppercase tracking-wider">● Open</span>
              )}
            </div>
            <h1 className="text-3xl md:text-5xl font-black mb-4 leading-tight">{selectedJob.title}</h1>
            <div className="flex flex-wrap gap-5 text-slate-300">
              <span className="flex items-center gap-2"><MapPin size={16} className="text-red-400" /> {selectedJob.location}</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-red-400" /> {selectedJob.type?.replace('_', ' ')}</span>
              <span className="flex items-center gap-2"><IndianRupee size={16} className="text-red-400" /> {formatSalary(selectedJob.salary_min, selectedJob.salary_max)}</span>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 space-y-8">
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 md:p-10">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Job Description</h2>
              <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed whitespace-pre-wrap">{selectedJob.description}</div>
            </div>

            {selectedJob.requirements && (
              <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 md:p-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Requirements</h2>
                <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed whitespace-pre-wrap">{selectedJob.requirements}</div>
              </div>
            )}

            {selectedJob.benefits && (
              <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 md:p-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Benefits</h2>
                <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed whitespace-pre-wrap">{selectedJob.benefits}</div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={() => openApplyForm(selectedJob)}
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold px-10 py-4 rounded-xl transition shadow-lg hover:scale-105 text-lg"
              >
                Apply Now
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── Job List View (default) ──────────────────────────
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading job opportunities...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 opacity-10 blur-3xl rounded-full"></div>
        <div className="max-w-5xl mx-auto px-4 relative z-10 text-center">
          <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-6"></div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-red-400 font-bold uppercase tracking-[0.2em] text-xs">Join Our Team</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tight">Careers at Ortus</h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Be part of a growing company that's building the future of food and home care products in India.
          </p>
        </div>
      </section>

      {/* Career Type Buttons */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">Choose Your Path</h2>
            <p className="text-slate-500">Select an option below to apply online</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CAREER_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.key}
                  onClick={() => openApplyForm(type.key)}
                  className="group relative bg-white rounded-2xl shadow-lg border border-slate-100 p-6 text-left hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${type.color}`}></div>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${type.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon size={26} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-red-600 transition">{type.label}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{type.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-red-600 group-hover:gap-2 transition-all">
                    Apply Now
                    <ArrowLeft size={14} className="rotate-180" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="h-1 w-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mx-auto mb-4"></div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">Testimonials</h2>
            <p className="text-slate-500">Hear what our community has to say</p>
          </div>

          {/* Testimonial Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {[
              { key: 'brand_ambassadors', label: 'Brand Ambassadors', icon: '⭐' },
              { key: 'brand_endorsers', label: 'Brand Endorsers', icon: '🎤' },
              { key: 'consumers', label: 'Consumers', icon: '🛒' },
              { key: 'dealers_retailers', label: 'Dealers / Retailers', icon: '🏪' },
              { key: 'distributor', label: 'Distributor', icon: '📦' },
              { key: 'super_distributors', label: 'Super Distributors', icon: '🏢' },
            ].map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveTestimonialTab(cat.key)}
                className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeTestimonialTab === cat.key
                    ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg scale-105'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS[activeTestimonialTab]?.map((t, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all duration-300">
                <Quote size={28} className="text-red-300 mb-3" />
                <p className="text-slate-600 text-sm leading-relaxed mb-4 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">{t.name}</h4>
                    <p className="text-xs text-slate-400">{t.role}</p>
                    <div className="flex gap-0.5 mt-1">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} size={12} className="text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )) ?? (
              <div className="col-span-full text-center py-12 text-slate-400">
                <p>Testimonials coming soon for this category.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job Listings by Career Type */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-black text-slate-900 mb-6 text-center">Open Positions</h2>
          {jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-200 rounded-3xl text-center bg-white">
              <div className="w-16 h-16 bg-slate-100 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Briefcase size={32} />
              </div>
              <p className="text-slate-600 font-medium text-lg mb-1">No open positions right now</p>
              <p className="text-slate-400 text-sm">Please check back later for new opportunities.</p>
            </div>
          ) : (
            <div className="space-y-10">
              {CAREER_TYPES.map((careerType) => {
                const typeJobs = jobs.filter(j => (j.department || 'employment') === careerType.key);
                if (typeJobs.length === 0) return null;
                const Icon = careerType.icon;
                return (
                  <div key={careerType.key}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${careerType.color} flex items-center justify-center text-white shrink-0`}>
                        <Icon size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900">{careerType.label}</h3>
                        <p className="text-xs text-slate-400">{typeJobs.length} open {typeJobs.length === 1 ? 'position' : 'positions'}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {typeJobs.map((job) => (
                        <div
                          key={job.id}
                          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl hover:border-red-200 transition-all cursor-pointer group"
                          onClick={() => openJobDetail(job)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1">
                              <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shrink-0">
                                <Briefcase size={22} />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900 group-hover:text-red-600 transition">{job.title}</h3>
                                <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                                  <span className="flex items-center gap-1.5"><MapPin size={14} className="text-red-400" /> {job.location}</span>
                                  <span className="flex items-center gap-1.5"><Clock size={14} /> {job.type?.replace('_', ' ')}</span>
                                  <span className="flex items-center gap-1.5"><IndianRupee size={14} className="text-red-400" /> {formatSalary(job.salary_min, job.salary_max)}</span>
                                </div>
                                {job.description && (
                                  <p className="text-sm text-slate-500 mt-3 line-clamp-2">
                                    {job.description.substring(0, 200)}
                                    {job.description.length > 200 ? '...' : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0">
                              <span className="inline-flex items-center px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold">
                                ● Open
                              </span>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-xs text-slate-400">
                              Posted: {new Date(job.published_at || job.created_at).toLocaleDateString()}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); openApplyForm(job); }}
                              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg text-sm transition shadow"
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {jobs.filter(j => !CAREER_TYPES.some(ct => ct.key === (j.department || 'employment'))).length > 0 && (
                <div>
                  <h3 className="text-lg font-black text-slate-900 mb-4">Other Positions</h3>
                  <div className="space-y-4">
                    {jobs.filter(j => !CAREER_TYPES.some(ct => ct.key === (j.department || 'employment'))).map((job) => (
                      <div
                        key={job.id}
                        className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl hover:border-red-200 transition-all cursor-pointer group"
                        onClick={() => openJobDetail(job)}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shrink-0">
                              <Briefcase size={22} />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-slate-900 group-hover:text-red-600 transition">{job.title}</h3>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                                <span className="flex items-center gap-1.5"><MapPin size={14} className="text-red-400" /> {job.location}</span>
                                <span className="flex items-center gap-1.5"><Clock size={14} /> {job.type?.replace('_', ' ')}</span>
                                <span className="flex items-center gap-1.5"><IndianRupee size={14} className="text-red-400" /> {formatSalary(job.salary_min, job.salary_max)}</span>
                              </div>
                              {job.description && (
                                <p className="text-sm text-slate-500 mt-3 line-clamp-2">
                                  {job.description.substring(0, 200)}
                                  {job.description.length > 200 ? '...' : ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="shrink-0">
                            <span className="inline-flex items-center px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold">
                              ● Open
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            Posted: {new Date(job.published_at || job.created_at).toLocaleDateString()}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); openApplyForm(job); }}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg text-sm transition shadow"
                          >
                            Apply Now
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Careers;
